import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { MessageModal } from '../../components/modals/MessageModal';
export function SignInPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setModalContent({
      title: 'Login Successful',
      message: 'Redirecting to 2FA verification...',
      type: 'success'
    });
    setIsMessageModalOpen(true);
    setTimeout(() => {
      navigate('/auth/verify-2fa', {
        state: {
          email: formData.email
        }
      });
    }, 1500);
  };
  const handleSocialLogin = (provider: string) => {
    setModalContent({
      title: `${provider} Login`,
      message: `Redirecting to ${provider} authentication... In production, this would use OAuth 2.0 flow.`,
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  return <div data-testid="signin-page" className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933]" data-id="element-2242">
      <header data-testid="signin-header" className="bg-white border-b border-gray-300 py-4 px-6 md:px-12" data-id="element-2243">
        <Link data-testid="signin-logo-link" to="/" className="flex items-center gap-2" data-id="element-2244">
          <div className="w-8 h-8 bg-[#1f2933] rounded-sm" data-id="element-2245"></div>
          <span className="font-bold text-xl tracking-tight" data-id="element-2246">
            Citiplots.com
          </span>
        </Link>
      </header>

      <main data-testid="signin-main" className="max-w-md mx-auto px-4 py-12" data-id="element-2247">
        <div data-testid="signin-card" className="bg-white border border-gray-300 p-8 shadow-sm" data-id="element-2248">
          <h1 data-testid="signin-title" className="text-2xl font-bold mb-2" data-id="element-2249">
            Welcome Back
          </h1>
          <p data-testid="signin-subtitle" className="text-sm text-gray-500 mb-6" data-id="element-2250">
            Sign in to your Citiplots account
          </p>

          <form data-testid="signin-form" onSubmit={handleSubmit} className="space-y-4" data-id="element-2251">
            <div data-id="element-2252">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2253">
                Email Address
              </label>
              <input data-testid="signin-email-input" type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2254" />
            </div>

            <div data-id="element-2255">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2256">
                Password
              </label>
              <div className="relative" data-id="element-2257">
                <input data-testid="signin-password-input" type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2258" />
                <button data-testid="signin-password-toggle-btn" type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2259">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-2260" /> : <Eye className="w-4 h-4" data-id="element-2261" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between" data-id="element-2262">
              <label className="flex items-center gap-2" data-id="element-2263">
                <input data-testid="signin-remember-checkbox" type="checkbox" checked={formData.rememberMe} onChange={e => setFormData({
                ...formData,
                rememberMe: e.target.checked
              })} data-id="element-2264" />
                <span className="text-xs text-gray-600" data-id="element-2265">Remember me</span>
              </label>
              <Link data-testid="signin-forgot-password-link" to="/auth/forgot-password" className="text-xs text-blue-600 hover:underline" data-id="element-2266">
                Forgot Password?
              </Link>
            </div>

            <button data-testid="signin-submit-btn" type="submit" className="w-full py-3 bg-[#1f2933] text-white font-bold hover:bg-gray-800" data-id="element-2267">
              Sign In
            </button>
          </form>

          <div data-testid="signin-social-section" className="mt-6" data-id="element-2268">
            <div className="relative" data-id="element-2269">
              <div className="absolute inset-0 flex items-center" data-id="element-2270">
                <div className="w-full border-t border-gray-300" data-id="element-2271"></div>
              </div>
              <div className="relative flex justify-center text-xs" data-id="element-2272">
                <span className="bg-white px-2 text-gray-500" data-id="element-2273">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2" data-id="element-2274">
              {['Google', 'LinkedIn', 'Facebook'].map(provider => <button key={provider} data-testid={`signin-${provider.toLowerCase()}-btn`} type="button" onClick={() => handleSocialLogin(provider)} className="py-2 border border-gray-300 text-xs font-medium hover:bg-gray-50" data-id="element-2275">
                  {provider}
                </button>)}
            </div>
          </div>

          <p data-testid="signin-signup-link" className="mt-6 text-center text-sm text-gray-600" data-id="element-2276">
            Don't have an account?{' '}
            <Link to="/auth/signup" className="text-blue-600 font-bold hover:underline" data-id="element-2277">
              Sign Up
            </Link>
          </p>
        </div>
      </main>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-2278" />
    </div>;
}